import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Play,
  Pause,
  Clock,
  Mail,
  AlertCircle,
  Loader2,
  Brain,
  ChevronDown,
  ChevronUp,
  MapPin
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  getAISummarySubscriptions,
  getAISummaryDefaults,
  createAISummarySubscription,
  updateAISummarySubscription,
  deleteAISummarySubscription,
  toggleAISummarySubscriptionStatus,
  testAISummarySubscription
} from '../../utils/api';
import { getLocationsByOrgId } from '../../utils/locationApi';

const normalizeCurrency = (value) => {
  const code = typeof value === 'string' ? value.trim().toUpperCase() : '';
  return /^[A-Z]{3}$/.test(code) ? code : 'USD';
};
const resolveReportingCurrencyForSelection = (selectedCurrencies, preferredCurrency) => {
  const normalizedSelected = (selectedCurrencies || []).map(normalizeCurrency);
  if (normalizedSelected.length === 0) return 'USD';
  if (normalizedSelected.length === 1) return normalizedSelected[0];
  const normalizedPreferred = normalizeCurrency(preferredCurrency);
  return normalizedSelected.includes(normalizedPreferred) ? normalizedPreferred : normalizedSelected[0];
};

const AISummaryManagement = ({ currentUser, currentOrganization, refreshTrigger }) => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [defaults, setDefaults] = useState(null);

  useEffect(() => {
    loadSubscriptions();
    loadDefaults();
  }, [currentUser, currentOrganization, refreshTrigger]);

  const loadDefaults = async () => {
    try {
      const data = await getAISummaryDefaults();
      setDefaults(data);
    } catch (err) {
      console.error('Error loading AI summary defaults:', err);
    }
  };

  const loadSubscriptions = async () => {
    try {
      setLoading(true);
      setError(null);

      if (currentOrganization?.id) {
        const data = await getAISummarySubscriptions({ orgId: currentOrganization.id });
        setSubscriptions(data);
      }
    } catch (err) {
      console.error('Error loading AI summary subscriptions:', err);
      setError('Failed to load AI summary subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (subscription) => {
    const newStatus = !subscription.isActive;
    const actionKey = `toggle-${subscription.id}`;

    try {
      setActionLoading(prev => ({ ...prev, [actionKey]: true }));
      const updated = await toggleAISummarySubscriptionStatus(subscription.id, newStatus);
      setSubscriptions(prev =>
        prev.map(sub => sub.id === subscription.id ? { ...sub, ...updated.subscription } : sub)
      );
      toast.success(`Subscription ${newStatus ? 'activated' : 'paused'}`);
    } catch (err) {
      console.error('Error toggling subscription status:', err);
      toast.error(`Failed to ${newStatus ? 'activate' : 'pause'} subscription`);
    } finally {
      setActionLoading(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  const handleDelete = async (subscription) => {
    if (!window.confirm('Are you sure you want to delete this AI summary subscription?')) {
      return;
    }

    const actionKey = `delete-${subscription.id}`;

    try {
      setActionLoading(prev => ({ ...prev, [actionKey]: true }));
      await deleteAISummarySubscription(subscription.id);
      setSubscriptions(prev => prev.filter(sub => sub.id !== subscription.id));
      toast.success('Subscription deleted');
    } catch (err) {
      console.error('Error deleting subscription:', err);
      toast.error('Failed to delete subscription');
    } finally {
      setActionLoading(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  const handleTest = async (subscription) => {
    const actionKey = `test-${subscription.id}`;

    try {
      setActionLoading(prev => ({ ...prev, [actionKey]: true }));
      const result = await testAISummarySubscription(subscription.id);
      toast.success(`Test AI summary sent successfully to ${result.recipients?.length || 0} recipient${result.recipients?.length !== 1 ? 's' : ''}`, {
        duration: 4000
      });
    } catch (err) {
      console.error('Error testing AI summary subscription:', err);
      toast.error('Failed to send test AI summary. Please try again.');
    } finally {
      setActionLoading(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  const handleEdit = (subscription) => {
    setEditingSubscription(subscription);
    setShowForm(true);
  };

  const handleCreateNew = () => {
    setEditingSubscription(null);
    setShowForm(true);
  };

  const handleFormSave = async (savedSubscription) => {
    setShowForm(false);
    setEditingSubscription(null);
    await loadSubscriptions();
    toast.success(editingSubscription ? 'Subscription updated' : 'Subscription created');
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingSubscription(null);
  };

  const formatTime = (time, timezone) => {
    if (!time) return 'Not set';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm} (${timezone || 'UTC'})`;
  };

  const formatNextScheduled = (dateStr) => {
    if (!dateStr) return 'Not scheduled';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 60 }}>
        <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ marginLeft: 12, color: '#6b7280' }}>Loading AI Summary subscriptions...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 24
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: '#1e293b' }}>
            <Brain size={20} style={{ marginRight: 8, verticalAlign: 'middle', color: '#6366f1' }} />
            AI Daily Summary
          </h2>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
            AI-powered daily operational recap emails
          </p>
        </div>
        <button
          onClick={handleCreateNew}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 16px',
            backgroundColor: '#6366f1',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer'
          }}
        >
          <Plus size={16} />
          New AI Summary
        </button>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: 8,
          padding: 16,
          marginBottom: 20,
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          <AlertCircle size={18} color="#dc2626" />
          <span style={{ color: '#dc2626' }}>{error}</span>
        </div>
      )}

      {/* Subscription List */}
      {subscriptions.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          backgroundColor: '#f8fafc',
          borderRadius: 12,
          border: '1px dashed #e2e8f0'
        }}>
          <Brain size={48} color="#94a3b8" style={{ marginBottom: 16 }} />
          <h3 style={{ margin: '0 0 8px', color: '#475569', fontSize: 18 }}>
            No AI Summary Subscriptions
          </h3>
          <p style={{ margin: '0 0 20px', color: '#64748b', fontSize: 14 }}>
            Create your first AI-powered daily operations summary email
          </p>
          <button
            onClick={handleCreateNew}
            style={{
              padding: '10px 20px',
              backgroundColor: '#6366f1',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            <Plus size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
            Create AI Summary
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {subscriptions.map(subscription => (
            <SubscriptionCard
              key={subscription.id}
              subscription={subscription}
              onToggle={() => handleToggleStatus(subscription)}
              onEdit={() => handleEdit(subscription)}
              onDelete={() => handleDelete(subscription)}
              onTest={() => handleTest(subscription)}
              isLoading={actionLoading[`toggle-${subscription.id}`] || actionLoading[`delete-${subscription.id}`]}
              isTestLoading={actionLoading[`test-${subscription.id}`]}
              formatTime={formatTime}
              formatNextScheduled={formatNextScheduled}
            />
          ))}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <AISummaryForm
          subscription={editingSubscription}
          currentOrganization={currentOrganization}
          currentUser={currentUser}
          defaults={defaults}
          onSave={handleFormSave}
          onCancel={handleFormCancel}
        />
      )}
    </div>
  );
};

const SubscriptionCard = ({
  subscription,
  onToggle,
  onEdit,
  onDelete,
  onTest,
  isLoading,
  isTestLoading,
  formatTime,
  formatNextScheduled
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{
      backgroundColor: 'white',
      border: '1px solid #e2e8f0',
      borderRadius: 12,
      overflow: 'hidden'
    }}>
      {/* Main Row */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: 16,
        gap: 16
      }}>
        {/* Status Indicator */}
        <div style={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          backgroundColor: subscription.isActive ? '#22c55e' : '#94a3b8',
          flexShrink: 0
        }} />

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Brain size={16} color="#6366f1" />
            <span style={{ fontWeight: 600, color: '#1e293b', fontSize: 15 }}>
              Daily Ops Summary
            </span>
            <span style={{
              backgroundColor: subscription.isActive ? '#dcfce7' : '#f3f4f6',
              color: subscription.isActive ? '#166534' : '#6b7280',
              fontSize: 11,
              padding: '2px 8px',
              borderRadius: 4,
              fontWeight: 500
            }}>
              {subscription.isActive ? 'Active' : 'Paused'}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 16, color: '#64748b', fontSize: 13 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <MapPin size={14} />
              {subscription.locationDetails?.length || subscription.locations?.length || 0} location(s)
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Clock size={14} />
              {formatTime(subscription.triggerTime?.time, subscription.triggerTime?.timezone)}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Mail size={14} />
              {subscription.recipients?.length || 0} recipient(s)
            </span>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            onClick={() => setExpanded(!expanded)}
            style={{
              padding: 8,
              backgroundColor: '#f1f5f9',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}
            title="Show details"
          >
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          <button
            onClick={onToggle}
            disabled={isLoading}
            style={{
              padding: 8,
              backgroundColor: subscription.isActive ? '#fef3c7' : '#dcfce7',
              border: 'none',
              borderRadius: 6,
              cursor: isLoading ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}
            title={subscription.isActive ? 'Pause' : 'Activate'}
          >
            {subscription.isActive ? <Pause size={16} color="#d97706" /> : <Play size={16} color="#16a34a" />}
          </button>
          <button
            onClick={onTest}
            disabled={isTestLoading || !subscription.isActive}
            style={{
              padding: 8,
              backgroundColor: subscription.isActive ? '#dbeafe' : '#f3f4f6',
              border: 'none',
              borderRadius: 6,
              cursor: isTestLoading || !subscription.isActive ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              opacity: subscription.isActive ? 1 : 0.5
            }}
            title={subscription.isActive ? 'Send test email' : 'Activate subscription to send test'}
          >
            {isTestLoading ? (
              <Loader2 size={16} color="#2563eb" style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <Mail size={16} color="#2563eb" />
            )}
          </button>
          <button
            onClick={onEdit}
            style={{
              padding: 8,
              backgroundColor: '#e0e7ff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}
            title="Edit"
          >
            <Edit size={16} color="#4f46e5" />
          </button>
          <button
            onClick={onDelete}
            disabled={isLoading}
            style={{
              padding: 8,
              backgroundColor: '#fee2e2',
              border: 'none',
              borderRadius: 6,
              cursor: isLoading ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}
            title="Delete"
          >
            <Trash2 size={16} color="#dc2626" />
          </button>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div style={{
          borderTop: '1px solid #e2e8f0',
          padding: 16,
          backgroundColor: '#f8fafc'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <h4 style={{ margin: '0 0 8px', fontSize: 13, color: '#64748b', fontWeight: 500 }}>
                Locations
              </h4>
              <div style={{ fontSize: 14, color: '#334155' }}>
                {subscription.locationDetails?.map(loc => loc.name).join(', ') ||
                 `${subscription.locations?.length || 0} location(s)`}
              </div>
            </div>
            <div>
              <h4 style={{ margin: '0 0 8px', fontSize: 13, color: '#64748b', fontWeight: 500 }}>
                Recipients
              </h4>
              <div style={{ fontSize: 14, color: '#334155' }}>
                {subscription.recipients?.join(', ') || 'None'}
              </div>
            </div>
            <div>
              <h4 style={{ margin: '0 0 8px', fontSize: 13, color: '#64748b', fontWeight: 500 }}>
                Next Scheduled
              </h4>
              <div style={{ fontSize: 14, color: '#334155' }}>
                {formatNextScheduled(subscription.nextScheduled)}
              </div>
            </div>
            <div>
              <h4 style={{ margin: '0 0 8px', fontSize: 13, color: '#64748b', fontWeight: 500 }}>
                Last Executed
              </h4>
              <div style={{ fontSize: 14, color: '#334155' }}>
                {subscription.lastExecuted
                  ? formatNextScheduled(subscription.lastExecuted)
                  : 'Never'}
              </div>
            </div>
          </div>

          {subscription.executionHistory?.lastError && (
            <div style={{
              marginTop: 12,
              padding: 12,
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 6
            }}>
              <div style={{ fontSize: 13, color: '#dc2626', fontWeight: 500 }}>
                Last Error: {subscription.executionHistory.lastError}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AISummaryForm = ({ subscription, currentOrganization, currentUser, defaults, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    locations: subscription?.locations || [],
    triggerTime: subscription?.triggerTime || { frequency: 'daily', time: '07:00', timezone: 'America/Chicago' },
    userPrompt: subscription?.userPrompt || defaults?.defaultUserPrompt || '',
    isActive: subscription?.isActive !== false,
    includeDetailedMetrics: subscription?.deliveryConfig?.includeDetailedMetrics !== false,
    customSubject: subscription?.deliveryConfig?.customSubject || '',
    // Default both channels on for new subscriptions and for legacy ones that
    // predate this field — matches the executor's backwards-compat default.
    channelEmail: !subscription?.deliveryChannels || subscription.deliveryChannels.includes('email'),
    channelPush: !subscription?.deliveryChannels || subscription.deliveryChannels.includes('push'),
    reportingCurrency: normalizeCurrency(subscription?.deliveryConfig?.reportingCurrency),
  });
  const [saving, setSaving] = useState(false);
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const selectedLocations = locations.filter((loc) => formData.locations.includes(loc.id));
  const selectedCurrencies = Array.from(new Set(selectedLocations.map((loc) => normalizeCurrency(loc.currency))));
  const isMultiCurrencySelection = selectedCurrencies.length > 1;
  const reportingCurrencyOptions = selectedCurrencies.length > 0 ? selectedCurrencies : ['USD'];

  // Load available locations using the same pattern as native reports
  useEffect(() => {
    const loadLocations = async () => {
      try {
        if (!currentOrganization?.id) return;
        setLoadingLocations(true);
        const locs = await getLocationsByOrgId(currentOrganization.id);
        setLocations(locs || []);
      } catch (error) {
        console.error('Error loading locations:', error);
        toast.error('Failed to load locations');
      } finally {
        setLoadingLocations(false);
      }
    };

    loadLocations();
  }, [currentOrganization]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.locations.length === 0) {
      toast.error('Please select at least one location');
      return;
    }

    const deliveryChannels = [
      formData.channelEmail && 'email',
      formData.channelPush && 'push',
    ].filter(Boolean);
    if (deliveryChannels.length === 0) {
      toast.error('Select at least one delivery method (email or push)');
      return;
    }

    setSaving(true);

    try {
      const reportingCurrency = resolveReportingCurrencyForSelection(
        selectedCurrencies,
        formData.reportingCurrency
      );
      const payload = {
        orgId: currentOrganization.id,
        locations: formData.locations,
        recipients: [currentUser?.email], // Always send to current user only
        triggerTime: formData.triggerTime,
        userPrompt: formData.userPrompt,
        isActive: formData.isActive,
        deliveryChannels,
        deliveryConfig: {
          includeDetailedMetrics: formData.includeDetailedMetrics,
          customSubject: formData.customSubject || null,
          reportingCurrency
        }
      };

      if (subscription) {
        await updateAISummarySubscription(subscription.id, payload);
      } else {
        await createAISummarySubscription(payload);
      }

      onSave();
    } catch (err) {
      console.error('Error saving subscription:', err);
      toast.error('Failed to save subscription');
    } finally {
      setSaving(false);
    }
  };

  const handleLocationToggle = (locationId) => {
    setFormData(prev => ({
      ...prev,
      locations: prev.locations.includes(locationId)
        ? prev.locations.filter(id => id !== locationId)
        : [...prev.locations, locationId]
    }));
  };

  const handleSelectAllLocations = () => {
    const allSelected = locations.length > 0 && formData.locations.length === locations.length;
    setFormData(prev => ({
      ...prev,
      locations: allSelected ? [] : locations.map(loc => loc.id)
    }));
  };

  const timezones = [
    { value: 'America/New_York', label: 'Eastern Time' },
    { value: 'America/Chicago', label: 'Central Time' },
    { value: 'America/Denver', label: 'Mountain Time' },
    { value: 'America/Los_Angeles', label: 'Pacific Time' },
    { value: 'UTC', label: 'UTC' }
  ];

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: 12,
        width: '100%',
        maxWidth: 700,
        maxHeight: '75vh',
        overflow: 'auto'
      }}>
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div style={{
            padding: '20px 24px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            gap: 12
          }}>
            <Brain size={24} color="#6366f1" />
            <div>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
                {subscription ? 'Edit' : 'Create'} AI Summary Subscription
              </h2>
              <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>
                Configure your daily AI-powered operations summary
              </p>
            </div>
          </div>

          {/* Body */}
          <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 24 }}>
            {/* Locations */}
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, fontSize: 14 }}>
                Properties
              </label>
              <div style={{
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                maxHeight: 200,
                overflow: 'auto',
                padding: 8
              }}>
                {loadingLocations ? (
                  <div style={{ padding: 12, color: '#64748b', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    Loading locations...
                  </div>
                ) : locations.length === 0 ? (
                  <div style={{ padding: 12, color: '#64748b', fontSize: 14 }}>
                    No locations available
                  </div>
                ) : (
                  <>
                    {/* All Locations checkbox */}
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px 12px',
                        cursor: 'pointer',
                        borderRadius: 6,
                        backgroundColor: formData.locations.length === locations.length ? '#eef2ff' : 'transparent',
                        borderBottom: '1px solid #e2e8f0',
                        marginBottom: 4
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={locations.length > 0 && formData.locations.length === locations.length}
                        onChange={handleSelectAllLocations}
                        style={{ marginRight: 10 }}
                      />
                      <span style={{ fontSize: 14, fontWeight: 500 }}>All Locations</span>
                    </label>
                    {/* Individual location checkboxes */}
                    {locations.map(location => (
                      <label
                        key={location.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderRadius: 6,
                          backgroundColor: formData.locations.includes(location.id) ? '#eef2ff' : 'transparent'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.locations.includes(location.id)}
                          onChange={() => handleLocationToggle(location.id)}
                          style={{ marginRight: 10 }}
                        />
                        <span style={{ fontSize: 14 }}>{location.name}</span>
                      </label>
                    ))}
                  </>
                )}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                {formData.locations.length} location(s) selected
              </div>
            </div>

            {/* Recipient Info (read-only) */}
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, fontSize: 14 }}>
                Recipient
              </label>
              <div style={{
                padding: '10px 12px',
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 6,
                fontSize: 14,
                color: '#334155',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}>
                <Mail size={16} color="#64748b" />
                {currentUser?.email || 'Your email'}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                Summary will be sent to your email address
              </div>
            </div>

            {/* Delivery methods */}
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, fontSize: 14 }}>
                Delivery
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.channelEmail}
                    onChange={(e) => setFormData(prev => ({ ...prev, channelEmail: e.target.checked }))}
                  />
                  <span style={{ fontSize: 14 }}>Email</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.channelPush}
                    onChange={(e) => setFormData(prev => ({ ...prev, channelPush: e.target.checked }))}
                  />
                  <span style={{ fontSize: 14 }}>Push notification (mobile app)</span>
                </label>
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                Push reaches recipients who have the Lodgeic mobile app installed and notifications enabled.
              </div>
            </div>

            {/* Schedule */}
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, fontSize: 14 }}>
                Schedule
              </label>
              <div style={{ display: 'flex', gap: 12 }}>
                <input
                  type="time"
                  value={formData.triggerTime.time}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    triggerTime: { ...prev.triggerTime, time: e.target.value }
                  }))}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: 6,
                    fontSize: 14
                  }}
                />
                <select
                  value={formData.triggerTime.timezone}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    triggerTime: { ...prev.triggerTime, timezone: e.target.value }
                  }))}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: 6,
                    fontSize: 14,
                    flex: 1
                  }}
                >
                  {timezones.map(tz => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </select>
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                Summary will be sent daily at the specified time
              </div>
            </div>

            {isMultiCurrencySelection && (
              <div>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, fontSize: 14 }}>
                  Reporting Currency
                </label>
                <select
                  value={formData.reportingCurrency}
                  onChange={(e) => setFormData(prev => ({ ...prev, reportingCurrency: normalizeCurrency(e.target.value) }))}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: 6,
                    fontSize: 14,
                    width: '100%'
                  }}
                >
                  {reportingCurrencyOptions.map((currency) => (
                    <option key={currency} value={currency}>{currency}</option>
                  ))}
                </select>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                  For multi-currency selections, AI summary amounts are generated in this reporting currency.
                </div>
              </div>
            )}

            {/* Active Status */}
            <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 24 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                />
                <span style={{ fontSize: 14 }}>Active (start sending immediately)</span>
              </label>
            </div>
          </div>

          {/* Footer */}
          <div style={{
            padding: '16px 24px',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 12
          }}>
            <button
              type="button"
              onClick={onCancel}
              style={{
                padding: '10px 20px',
                backgroundColor: '#f1f5f9',
                border: 'none',
                borderRadius: 6,
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: '10px 20px',
                backgroundColor: '#6366f1',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                fontSize: 14,
                fontWeight: 500,
                cursor: saving ? 'wait' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              {saving && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />}
              {subscription ? 'Update' : 'Create'} Subscription
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AISummaryManagement;
